import { memo, useCallback, useContext, useEffect, useState, type FunctionComponent, type ReactNode } from 'react';
import map from 'lodash/map';
import size from 'lodash/size';
import { useMutation } from '@apollo/client';
// Material UI imports
import type { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
// Skillmore UI Components
import useMutationMethod from '@empathco/ui-components/src/hooks/useMutationMethod';
import CardSection from '@empathco/ui-components/src/elements/CardSection';
import ActionSnackbar from '@empathco/ui-components/src/elements/ActionSnackbar';
import FetchFailedAlert from '@empathco/ui-components/src/elements/FetchFailedAlert';
import InlineAlert from '@empathco/ui-components/src/elements/InlineAlert';
import LoadingPlaceholder from '@empathco/ui-components/src/elements/LoadingPlaceholder';
import SkillsGapLegend from '@empathco/ui-components/src/elements/SkillsGapLegend';
import ReloadingOverlay from '@empathco/ui-components/src/elements/ReloadingOverlay';
// local imports
import { NEW_OPPORTUNITY_BOOKING } from '../graphql/NewOpportunityBooking';
import { UPDATE_OPPORTUNITY_BOOKING } from '../graphql/UpdateOpportunityBooking';
import { UPDATE_OPPORTUNITY_MATCH } from '../graphql/UpdateOpportunityMatch';
import { UPDATE_OPPORTUNITY } from '../graphql/UpdateOpportunity';
import {
  BookingStatus, MyOpportunityStatus, OpportunityStatus, Opportunity, OpportunityMatch, MyOpportunity,
  NewOpportunityBookingDocument, UpdateOpportunityBookingDocument, UpdateOpportunityMatchDocument, UpdateOpportunityDocument
} from '../graphql/types';
import { GlobalContext } from '../context/global';
import PaginationControls, { hasPagination, PaginationControlsComponent } from '../v3/PaginationControls';
import OpportunityBookingCard from '../v3/OpportunityBookingCard';

const legendSx: SxProps<Theme> = { pb: 2.5 } as const;

type OpportunityBookingPanelProps = {
  readonly supervisor?: boolean;
  readonly readOnly?: boolean;
  readonly opportunity?: Opportunity | null;
  readonly disabled?: boolean | null;
  readonly bookings?: (OpportunityMatch | MyOpportunity)[] | null;
  readonly count?: number | null;
  readonly pending?: boolean | null;
  readonly failed?: boolean | null;
  readonly onBookingRequest?: (opp: MyOpportunity, reject?: boolean) => void;
  readonly onRemoveApplication?: (opp: MyOpportunity) => void;
  readonly onEndOpportunity?: (opp: MyOpportunity) => void;
  readonly confirmingId?: number | null;
  readonly rejectingId?: number | null;
  readonly removingId?: number | null;
  readonly filters?: ReactNode;
  readonly currentPage: number;
  readonly onPageSelected: (page: number) => void;
  readonly onPageSize: (pageSize: number) => void;
  // for Strorybook only
  readonly testAction?: number;
}

const OpportunityBookingPanel: FunctionComponent<OpportunityBookingPanelProps> = ({
  supervisor = false,
  readOnly = false,
  opportunity,
  disabled: parentDisabled = false,
  bookings,
  count,
  pending = false,
  failed = false,
  onBookingRequest,
  onRemoveApplication,
  onEndOpportunity,
  confirmingId,
  rejectingId,
  removingId,
  filters,
  currentPage,
  onPageSelected,
  onPageSize,
  testAction
  // eslint-disable-next-line complexity, max-lines-per-function
}) => {
  const { paths: { supvEmplPath } } = useContext(GlobalContext);

  const { id: opportunity_id } = opportunity || {};

  const { mutate: newRequest, loading: requestPending, failed: requestFailed } = useMutationMethod({
    // TODO: key: 'newOpportunityBooking', -- restore when backend responds with `success: true`
    mutation: useMutation(NEW_OPPORTUNITY_BOOKING as typeof NewOpportunityBookingDocument)
  });

  const { mutate: updateBooking, loading: bookingPending } = useMutationMethod({
    // TODO: key: 'updateOpportunityBooking', -- restore when backend responds with `success: true`
    mutation: useMutation(UPDATE_OPPORTUNITY_BOOKING as typeof UpdateOpportunityBookingDocument)
  });

  const { mutate: rejectBooking, loading: rejectPending } = useMutationMethod({
    // TODO: key: 'updateOpportunityBooking', -- restore when backend responds with `success: true`
    mutation: useMutation(UPDATE_OPPORTUNITY_BOOKING as typeof UpdateOpportunityBookingDocument)
  });

  const { mutate: deleteBooking, loading: deletePending } = useMutationMethod({
    // TODO: key: 'updateOpportunityBooking', -- restore when backend responds with `success: true`
    mutation: useMutation(UPDATE_OPPORTUNITY_BOOKING as typeof UpdateOpportunityBookingDocument)
  });

  const { mutate: updateMatch, loading: matchPending, failed: matchFailed } = useMutationMethod({
    // TODO: key: 'updateOpportunityMatch', // -- restore when backend responds with `success: true`
    mutation: useMutation(UPDATE_OPPORTUNITY_MATCH as typeof UpdateOpportunityMatchDocument)
  });

  const { mutate: updateOpportunity, loading: updatePending, failed: updateFailed } = useMutationMethod({
    // TODO: key: 'updateOpportunity', -- restore when backend responds with `success: true`
    mutation: useMutation(UPDATE_OPPORTUNITY as typeof UpdateOpportunityDocument)
  });

  const [pendingMatchId, setPendingMatchId] = useState<number | null>(null);
  // one failure state for all three booking writes, cleared before each so the snackbar re-opens every time
  const [bookingUpdateFailed, setBookingUpdateFailed] = useState(false);

  const handleRemove = useCallback((match_id: number) => {
    if (opportunity_id && match_id) {
      setPendingMatchId(match_id);
      updateMatch?.({
        variables: { opportunity_id, match_id, input: { owner_status: MyOpportunityStatus.active } },
        // TODO: optimistic response
        update: (cache) => {
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'opportunityMatches' });
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'opportunityBookings' });
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'opportunities' }); // need to update counts
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'opportunity' }); // need to update counts
        },
        onCompleted: () => setPendingMatchId(null),
        onError: () => setPendingMatchId(null)
      });
    }
  }, [opportunity_id, updateMatch]);

  const handleDelete = useCallback((match_id: number, booking_id?: number, shortlisted?: boolean) => {
    if (opportunity_id && match_id) {
      setPendingMatchId(match_id);
      setBookingUpdateFailed(false);
      if (booking_id) {
        deleteBooking?.({
          variables: { opportunity_id, booking_id, input: { status: BookingStatus.deleted } },
          // TODO: optimistic response
          update: (cache) => {
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'opportunityBookings' });
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'opportunities' }); // need to update counts
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'opportunity' }); // need to update counts
          },
          onCompleted: () => {
            if (shortlisted) handleRemove(match_id); // remove from shortlist
            else setPendingMatchId(null);
          },
          onError: () => {
            setPendingMatchId(null);
            setBookingUpdateFailed(true);
          }
        });
      } else /* if (shortlisted) -- must be `true` anyway */ handleRemove(match_id);
    }
  }, [opportunity_id, handleRemove, deleteBooking]);

  const handleRequest = useCallback((oppMatch: OpportunityMatch, reject = false) => {
    if (!opportunity_id || !oppMatch) return;
    const { id: booking_id } = oppMatch.booking || {};
    const status = reject ? BookingStatus.manager_rejected : BookingStatus.manager_requested;
    setBookingUpdateFailed(false);
    if (booking_id) {
      setPendingMatchId(oppMatch.id);
      (reject ? rejectBooking : updateBooking)?.({
        variables: { opportunity_id, booking_id, input: { status } },
        // TODO: optimistic response
        update: (cache) => {
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'opportunityBookings' });
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'opportunityMatches' });
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'opportunities' }); // need to update counts
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'opportunity' }); // need to update counts
        },
        onCompleted: () => setPendingMatchId(null),
        onError: () => {
          setPendingMatchId(null);
          setBookingUpdateFailed(true);
        }
      });
    } else {
      const { id: employee_id } = oppMatch.employee || {};
      if (employee_id) {
        setPendingMatchId(oppMatch.id);
        newRequest?.({
          variables: { opportunity_id, input: { employee_id, status } },
          // TODO: optimistic response
          update: (cache) => {
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'opportunityBookings' });
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'opportunityMatches' });
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'opportunities' }); // need to update counts
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'opportunity' }); // need to update counts
          },
          onCompleted: () => setPendingMatchId(null),
          onError: () => setPendingMatchId(null)
        });
      }
    }
  }, [opportunity_id, newRequest, updateBooking, rejectBooking]);

  const handleOpportunityStatus = useCallback((matchId: number, status: OpportunityStatus) => {
    // Manager can only Start or Archive an opportunity from Booking tab
    if (opportunity_id && (status === OpportunityStatus.started || status === OpportunityStatus.archived)) {
      setPendingMatchId(matchId);
      updateOpportunity?.({
        variables: { opportunity_id, input: { status } },
        // TODO: optimistic response
        update: (cache) => {
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'opportunities' });
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'opportunity' });
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'opportunityMatches' });
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'opportunityBookings' });
        },
        onCompleted: () => setPendingMatchId(null),
        onError: () => setPendingMatchId(null)
      });
    }
  }, [opportunity_id, updateOpportunity]);

  useEffect(() => {
    if (testAction) {
      if (testAction === 1) {
        setPendingMatchId(15);
        newRequest?.({
          variables: { opportunity_id: 444, input: { employee_id: 304, status: BookingStatus.manager_requested } },
          onError: () => setPendingMatchId(null)
        });
      } else if (testAction === 2 || testAction === 3) {
        setPendingMatchId(14);
        (testAction === 3 ? rejectBooking : updateBooking)?.({
          variables: { opportunity_id: 444, booking_id: 123779, input: { status: BookingStatus.manager_requested } },
          onError: () => {
            setPendingMatchId(null);
            setBookingUpdateFailed(true);
          }
        });
      } else if (testAction === 4) {
        setPendingMatchId(14);
        updateMatch?.({
          variables: { opportunity_id: 444, match_id: 14, input: { owner_status: MyOpportunityStatus.active } },
          onError: () => setPendingMatchId(null)
        });
      } else if (testAction === 5 || testAction === 6) {
        setPendingMatchId(12);
        updateOpportunity?.({
          variables: { opportunity_id: 444, input: {
            status: testAction === 5 ? OpportunityStatus.started : OpportunityStatus.archived
          } },
          onError: () => setPendingMatchId(null)
        });
      }
    }
  }, [testAction, newRequest, updateBooking, rejectBooking, updateMatch, updateOpportunity]);

  const disabled = parentDisabled || requestPending || bookingPending || rejectPending || deletePending ||
    matchPending || updatePending;

  const pagination = (
    <PaginationControls
        settingsId={supervisor ? 'opp_booking' : 'my_opportunities'}
        loaded={Boolean(bookings)}
        pending={pending}
        loading={pending}
        total={count}
        currentPage={currentPage}
        onPageSelected={onPageSelected}
        onPageSize={onPageSize}
        disabled={disabled || failed}
    />
  );

  const withPagination = hasPagination(pagination as PaginationControlsComponent);
  const loading = pending && !bookings;
  const reloading = Boolean(pending && bookings);

  return (
    <>
      {filters ? (
        <CardSection narrow top={!failed && !loading} flex>
          {filters}
        </CardSection>
      ) : undefined}
      {(failed && <FetchFailedAlert flat/>) ||
      (loading && <LoadingPlaceholder flat/>) || (
        <ReloadingOverlay reloading={reloading}>
          {failed || loading ? undefined : (size(bookings) < 1 && (
            <InlineAlert severity="info" message={supervisor ? 'opportunities.booking.empty' : 'opportunities.my.empty'} flat/>
          )) || (
            <CardSection>
              {supervisor ? <SkillsGapLegend withGrowth sx={legendSx}/> : undefined}
              <Box display="flex" flexDirection="column">
                {/* eslint-disable-next-line complexity */}
                {map(bookings, (booking) => (
                  <OpportunityBookingCard
                      key={booking.id}
                      supervisor={supervisor}
                      item={booking}
                      opportunity={opportunity}
                      disabled={disabled}
                      // manager
                      route={supervisor ? supvEmplPath : undefined}
                      readOnly={readOnly}
                      onRequest={supervisor ? handleRequest : undefined}
                      onRemove={supervisor ? handleRemove : undefined}
                      onDelete={supervisor ? handleDelete : undefined}
                      onOpportunityStatus={supervisor ? handleOpportunityStatus : undefined}
                      statusPending={updatePending && pendingMatchId === booking.id ? true : undefined}
                      deletePending={deletePending && pendingMatchId === booking.id ? true : undefined}
                      // employee
                      onBookingRequest={supervisor ? undefined : onBookingRequest}
                      onRemoveApplication={supervisor ? undefined : onRemoveApplication}
                      onEndOpportunity={supervisor ? undefined : onEndOpportunity}
                      // manager & employee
                      requestPending={((requestPending || bookingPending) && pendingMatchId === booking.id) ||
                        (confirmingId && confirmingId === booking.id)
                        ? true : undefined}
                      rejectPending={(rejectPending && pendingMatchId === booking.id) ||
                        (rejectingId && rejectingId === booking.id)
                        ? true : undefined}
                      removePending={(matchPending && pendingMatchId === booking.id) ||
                        (removingId && removingId === booking.id)
                        ? true : undefined}
                  />
                ))}
              </Box>
            </CardSection>
          )}
        </ReloadingOverlay>
      )}
      {withPagination ? (
        <CardSection narrow flex bottom={!loading}>
          {pagination}
        </CardSection>
      ) : pagination}
      <ActionSnackbar
          severity="error"
          message="opportunities.booking.new.error"
          open={requestFailed}
      />
      <ActionSnackbar
          severity="error"
          message="opportunities.booking.update.error"
          open={bookingUpdateFailed}
      />
      <ActionSnackbar
          severity="error"
          message="opportunities.matches.update.error"
          open={matchFailed}
      />
      <ActionSnackbar
          severity="error"
          message="opportunities.update.error"
          open={updateFailed}
      />
    </>
  );
};

export default memo(OpportunityBookingPanel);
